// SPDX-License-Identifier: Apache-2.0

// tether-brain v2 — storage schema (Hephaestus / forge-stream).
//
// Pure-type module. No runtime dependencies — Solid components, the Dexie DB,
// the Yjs binding, and the WGSL search kernels (Prometheus's stream) all import
// these shapes and stay decoupled from each other.
//
// Frontmatter is basic-memory-aligned, mirroring `~/.brain/CLAUDE.md`:
//   title / type / tags / permalink / sensitivity / created
// Optional Brain Hub extras (slot, status, project) survive round-trips so the
// 3D vault orbit can re-render the same UI it does today off the old store.

// ─── sensitivity / Veil tier ────────────────────────────────────────────────

export type Tier = 'public' | 'internal' | 'private' | 'secret';

export const TIERS: readonly Tier[] = ['public', 'internal', 'private', 'secret'] as const;

export function isTier(x: unknown): x is Tier {
  return typeof x === 'string' && (TIERS as readonly string[]).includes(x);
}

// ─── frontmatter (basic-memory aligned) ─────────────────────────────────────

/** Mirrors the `---` block convention from `~/.brain/CLAUDE.md`. Fields are
 *  optional because hand-typed notes in the SPA may skip them; the Veil layer
 *  fills in slot defaults from `.brain/config.yaml` at fetch time. */
export interface VaultFrontmatter {
  title?: string;
  type?:
    | 'project'
    | 'concept'
    | 'paper'
    | 'experiment'
    | 'meeting'
    | 'persona'
    | 'daily'
    | 'person'
    | 'note'
    // Brain Hub-specific values that flow in from old tether imports.
    | 'claude-session'
    | 'agent';
  tags?: string[];
  permalink?: string;
  sensitivity?: Tier;
  /** ISO `YYYY-MM-DD`. */
  created?: string;
  /** Top-level vault slot (`projects`, `experiments`, `cd`, …). */
  slot?: string;
  /** Catch-all for legacy keys we want to preserve verbatim. */
  [key: string]: unknown;
}

// ─── visual / vault-store-original extras ───────────────────────────────────

export type VaultTint = 'cyan' | 'violet' | 'amber' | 'rose';

/** Live Claude session metadata — populated only when `type:'claude-session'`. */
export interface ClaudeSessionExtras {
  pid?: number;
  project?: string;
  status?: string;
  cost_usd?: number;
  context_pct?: number;
  files_modified?: Array<[string, number]>;
}

// ─── docs ────────────────────────────────────────────────────────────────────

/** A single note in the vault. `id` is a stable browser-local UUID; `permalink`
 *  is the basic-memory slug used by the MCP server when round-tripping to
 *  `~/brain`. The two diverge intentionally — the browser must be able to mint
 *  ids before disk writes succeed. */
export interface VaultDoc extends ClaudeSessionExtras {
  id: string;
  title: string;
  /** Markdown body (NOT including frontmatter — we keep it parsed in `front`). */
  content: string;
  /** Parsed frontmatter; freshly minted notes get `{}` until the user fills it. */
  front: VaultFrontmatter;
  tint: VaultTint;
  createdAt: number;
  updatedAt: number;
  /** byte length of `content`, kept on the row so list views can sort by size
   *  without rehydrating the body. */
  bytes?: number;
  /** Source FSAccess path relative to the granted directory handle. Optional —
   *  notes typed inline in the SPA never get a path until first save. */
  path?: string;
}

// ─── int8 embeddings ─────────────────────────────────────────────────────────

/** One row per (doc, model). Symmetric per-vector quantization: the original
 *  fp32 vector v is stored as `vec[i] = round(v[i] * 127 / scale)` where
 *  `scale = max(|v|)`. Reconstruct fp32 with `v = vec * (scale / 127)`. */
export interface VaultEmbedding {
  /** Composite primary key: `${docId}::${model}`. */
  id: string;
  docId: string;
  /** e.g. `"multilingual-e5-small"`. */
  model: string;
  /** Vector dimension. The Daedalus block fixes 384 for e5-small but other
   *  models (e.g. nomic-embed @ 768) plug in unchanged. */
  dim: number;
  /** Length-`dim` int8 buffer, persisted as a typed-array view. */
  vec: Int8Array;
  /** Per-vector dequant scale: `fp32 = int8 * (scale / 127)`. */
  scale: number;
  /** ms epoch when computed. */
  computedAt: number;
}

// ─── TF-IDF (CPU fallback for v1; Prometheus's tfidfSpMV swaps in later) ────

/** Inverted-index posting: which docs contain `term`, and with what TF. */
export interface TfidfPosting {
  /** Composite key: `${term}::${docId}`. */
  id: string;
  term: string;
  docId: string;
  /** Raw term frequency (count). IDF lives on the vocab row. */
  tf: number;
}

export interface TfidfVocab {
  /** Token. Lower-cased, stop-words pre-filtered. */
  term: string;
  /** Document frequency (number of docs that contain `term`). */
  df: number;
  /** Cached `log(N / df)` for the corpus N at last-build time. Recompute when N
   *  shifts > 10%. */
  idf: number;
}

// ─── graph edges + adjacency ────────────────────────────────────────────────

export type EdgeKind =
  /** From `[[wikilink]]` or `[md](other.md)`. */
  | 'link'
  /** Cosine-similar pair from the embedding pass. */
  | 'embedding'
  /** Cosine-similar pair from the TF-IDF pass. */
  | 'tfidf'
  /** Backlink (auto-derived; redundant with link but useful for ego queries). */
  | 'backlink';

export interface VaultEdge {
  /** Composite key: `${a}::${b}::${kind}` where a < b for undirected. */
  id: string;
  a: string;
  b: string;
  kind: EdgeKind;
  /** Edge weight; cosine similarity for `embedding`/`tfidf`, 1 for hard links. */
  w: number;
  /** ms epoch. */
  computedAt: number;
}

/** k-hop adjacency cache — Prometheus's kHopBFS reads/writes this. */
export interface AdjacencyEntry {
  /** Composite key: `${docId}::${k}`. */
  id: string;
  docId: string;
  /** Hop count. */
  k: number;
  /** Doc ids reachable within `k` hops, sorted ascending for diff-friendliness. */
  neighbors: string[];
  computedAt: number;
}

// ─── Yjs snapshot blob ──────────────────────────────────────────────────────

/** One snapshot per doc; idle-time write of `Y.encodeStateAsUpdate(yDoc)`.
 *  The browser keeps the live `Y.Doc` in memory; this row is the on-restart
 *  rehydration source. */
export interface YjsSnapshot {
  /** Same as `VaultDoc.id`. */
  docId: string;
  /** `Y.encodeStateAsUpdate(doc)` — full state, not delta. */
  state: Uint8Array;
  updatedAt: number;
}

// ─── model shards (transformers.js fp16 weights, sharded into Dexie) ────────

/** A WebLLM / transformers.js weight shard. We persist them in Dexie rather
 *  than the Cache API so quota is unified with the rest of the vault. */
export interface ModelShard {
  /** Composite key: `${model}::${shard}`. */
  id: string;
  /** e.g. `"Xenova/multilingual-e5-small"`. */
  model: string;
  /** Shard index (0-based). */
  shard: number;
  /** Total shard count for this model (sanity-check on rehydration). */
  totalShards: number;
  /** Raw bytes; size-bounded by quota policy in `dexie-schema.ts`. */
  bytes: Uint8Array;
  /** SHA-256 hex of `bytes`; matched against the model manifest at load. */
  sha256: string;
  /** ms epoch. */
  fetchedAt: number;
}

// ─── search results (returned by VaultStore.searchDocs) ─────────────────────

export interface SearchHit {
  doc: VaultDoc;
  /** Score in [0, 1]; comparable across hits in the same query, not across
   *  queries (TF-IDF and cosine are normalized differently). */
  score: number;
  /** `tfidf` for the v1 CPU fallback; `embedding` once the WGSL kernel lands. */
  source: 'tfidf' | 'embedding';
}

// ─── ingest ──────────────────────────────────────────────────────────────────

export type IngestKind = 'md' | 'txt' | 'docx' | 'pdf' | 'xlsx' | 'csv' | 'udf';

export interface IngestResult {
  title: string;
  /** Markdown body (sans frontmatter). */
  content: string;
  /** Parsed frontmatter (md only — other kinds yield `{}`). */
  front: VaultFrontmatter;
  kind: IngestKind;
  /** Optional FSAccess path relative to the granted vault directory. */
  path?: string;
}
